import { Button } from '@/components/ui/button';
import {
  FEATURE_CATEGORIES,
  FeatureCategoryId,
  useFeaturesCategory,
} from '@/providers/features-category.provider';
import { navLinks } from '@/utils/data/nav-links';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export function MobileMenu({ isActive, onClose }: { isActive: boolean; onClose: () => void }) {
  const [mounted, setMounted] = useState(false);
  const [isFeaturesExpanded, setIsFeaturesExpanded] = useState(false);
  const router = useRouter();
  const { goToCategory, goToFacturationElectronique, setActiveCategory } = useFeaturesCategory();

  const handleCategorySelect = (id: FeatureCategoryId) => {
    setIsFeaturesExpanded(false);
    onClose();
    if (router.pathname !== '/') {
      setActiveCategory(id);
      void router.push('/#fonctionnalites', undefined, { scroll: false });
      return;
    }
    goToCategory(id);
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return createPortal(
    <>
      {/* Overlay */}
      <div
        style={{ zIndex: 100 }}
        className={[
          'fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300',
          isActive ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        ].join(' ')}
        onClick={onClose}
      />

      {/* Drawer */}
      <nav
        className={clsx(
          'fixed top-0 right-0 z-200 flex h-screen w-[80vw] max-w-sm flex-col bg-white shadow-2xl transition-transform duration-400',
          isActive ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <ul className="flex flex-1 flex-col gap-1 px-8 pt-28 pb-6">
          {navLinks.map((link, i) => {
            if (link.href === '/#fonctionnalites') {
              return (
                <li key={i} className="block overflow-hidden">
                  <button
                    className={clsx(
                      'flex w-full items-center justify-between border-b border-gray-100 py-4 text-base font-medium text-gray-800 transition-all duration-400',
                      isActive ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0',
                    )}
                    style={{ transitionDelay: `${i * 50}ms` }}
                    onClick={() => setIsFeaturesExpanded((v) => !v)}
                  >
                    <span>{link.label}</span>
                    <svg
                      aria-hidden="true"
                      className={clsx(
                        'text-gray-400 transition-transform duration-300',
                        isFeaturesExpanded && 'rotate-180',
                      )}
                      fill="none"
                      height="16"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                      width="16"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>
                  <div
                    className={clsx(
                      'overflow-hidden transition-all duration-300',
                      isFeaturesExpanded ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0',
                    )}
                  >
                    <ul className="py-1 pl-2">
                      {FEATURE_CATEGORIES.map((category) => (
                        <li key={category.id}>
                          <button
                            className="flex w-full items-center gap-2.5 py-2.5 pl-4 text-sm text-gray-600 transition-colors duration-200 hover:text-orange"
                            onClick={() => handleCategorySelect(category.id)}
                          >
                            <span className="h-1 w-1 shrink-0 rounded-full bg-orange/50" />
                            {category.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              );
            }

            return (
              <li key={i} className="block overflow-hidden">
                <Button
                  className={clsx(
                    'flex items-center border-b border-gray-100 py-4 text-base font-medium text-gray-800 transition-all duration-400',
                    isActive ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0',
                  )}
                  href={link.href}
                  isAnchor
                  style={{
                    transitionDelay: `${i * 50}ms`,
                  }}
                  target={link.target}
                  variant="transparent"
                  onClick={(event) => {
                    if (link.href !== '/#facturation-electronique') {
                      onClose();
                      return;
                    }

                    event.preventDefault();
                    onClose();

                    if (router.pathname !== '/') {
                      setActiveCategory('facturation');
                      void router.push(link.href, undefined, { scroll: false });
                      return;
                    }

                    goToFacturationElectronique();
                  }}
                >
                  {link.label}
                </Button>
              </li>
            );
          })}
        </ul>

        <div className="flex flex-col gap-3 px-8 pb-10">
          <Button href="tel:330184257027" variant="transparent">
            01 84 25 70 27
          </Button>
          <Button href="/contact/" variant="fill" onClick={onClose}>
            Demandez une démo
          </Button>
        </div>
      </nav>
    </>,
    document.body,
  );
}
